<script setup lang="ts">
import type { Schemas } from "#shopware"
import type { BoxLayout } from "@shopware/composables"
import { getProductName } from "@shopware/helpers"

interface ProductCardImageProps {
    product: Schemas["Product"]
    layoutType?: BoxLayout,
    imageSizes?: string
    width?: string | number
    height?: string | number
    position?: number
    prioritizeFirst?: boolean
}

const props = withDefaults(defineProps<ProductCardImageProps>(), {
    layoutType: 'standard',
    imageSizes: undefined,
    width: 345,
    height: 392,
    position: undefined,
    prioritizeFirst: false
})

const emit = defineEmits<{
    loaded: []
}>()

const onImageLoad = () => {
    emit('loaded')
}

const imageClasses = computed(() =>
    `w-auto object-contain overflow-hidden ${props.layoutType === 'image' ? 'h-[380px]' : 'h-[290px]'}`
)

// Loading strategy based on position and prioritizeFirst flag
// Only prioritize (fetchpriority="high" + loading="eager") when explicitly requested AND it's the first item
const isFirstPrioritizedImage = computed(() => props.prioritizeFirst && props.position === 1)
// Use intersection lazy loading for items after the first when prioritizeFirst is enabled
const shouldUseLazyLoading = computed(() => props.prioritizeFirst && props.position !== undefined && props.position > 1)

</script>

<template>
    <FoundationImage
        :src="product?.cover?.media?.url"
        :alt="getProductName({ product }) || ''"
        :class="imageClasses"
        data-testid="product-card-image"
        :width="width"
        :height="height"
        fit="fit"
        :sizes="imageSizes"
        :modifiers="{ extend: 1, trim: 1 }"
        :intersection-lazy="shouldUseLazyLoading"
        :preload="isFirstPrioritizedImage"
        :fetchpriority="isFirstPrioritizedImage ? 'high' : undefined"
        :loading="isFirstPrioritizedImage ? 'eager' : (!prioritizeFirst ? 'lazy' : undefined)"
        root-margin="200px"
        @load="onImageLoad"
    />
</template>
